/* eslint-disable no-useless-catch */
/* eslint-disable react/prop-types */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMap
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerIconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from './../../../assets/img/icons/location.png';
import AppContext from 'context/Context';
import { isEmpty } from 'lodash';
import { Button } from 'react-bootstrap';
import { IoIosClose } from 'react-icons/io';

// Setting the default marker icon
L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 29],
  iconAnchor: [12, 27],
  popupAnchor: [1, -24],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
  shadowAnchor: [10, 40]
});

const boxStyle = {
  position: 'absolute',
  top: '3%',
  right: '1%',
  width: '300px',
  height: '95%',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  borderRadius: '10px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  color: '#000',
  fontSize: '1.2em',
  textAlign: 'center',
  zIndex: 800,
  flexDirection: 'column',
  padding: '20px',
  overflow: 'hidden'
};

const CenterMapOnPolygon = React.memo(({ geoJsonData }) => {
  const map = useMap();

  useEffect(() => {
    if (geoJsonData && geoJsonData.length > 0) {
      const geoJsonLayer = L.geoJSON(geoJsonData);
      const bounds = geoJsonLayer.getBounds();

      // Check if bounds are valid
      if (bounds.isValid()) {
        // Use fitBounds with padding to ensure the polygon fits within the container
        map.fitBounds(bounds, {
          padding: [30, 30], // Add padding around the bounds
          maxZoom: 15 // Limit the max zoom level
        });
      } else {
        console.warn('Invalid bounds for the GeoJSON data');
      }
    }
  }, [geoJsonData, map]);

  return null;
});

const FarmSelectionComponent = React.memo(({ data, handleSearch }) => {
  const {
    config: { isRTL }
  } = useContext(AppContext);
  const [selectedPolygons, setSelectedPolygons] = useState([]);

  useEffect(() => {
    setSelectedPolygons([]);
  }, [data]);

  const handlePolygonClick = useCallback(feature => {
    const polygonId = feature.properties.id;
    if (feature.id == 0) return;
    setSelectedPolygons(prevPolygons => {
      const isAlreadySelected = prevPolygons.some(
        polygon => polygon.id === polygonId
      );
      if (isAlreadySelected) {
        return prevPolygons.filter(polygon => polygon.id !== polygonId);
      } else if (prevPolygons.length < 3) {
        const newPolygon = {
          id: polygonId,
          area: feature.properties.project_area,
          farmLandID: feature.properties.project_id,
          farmLandName: feature.properties.project_name,
          farmerName: feature.properties.farmer_name
        };
        return [...prevPolygons, newPolygon];
      }
      return prevPolygons;
    });
  }, []);

  const getPolygonCentroid = useCallback(feature => {
    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
      const polygon = L.polygon(geometry.coordinates);
      const center = polygon.getBounds().getCenter();
      return [center.lng, center.lat];
    } else if (geometry.type === 'MultiPolygon') {
      const centroids = geometry.coordinates.map(polygonCoords => {
        const polygon = L.polygon(polygonCoords);
        const center = polygon.getBounds().getCenter();
        return [center.lat, center.lng];
      });
      const avgLat =
        centroids.reduce((sum, centroid) => sum + centroid[0], 0) /
        centroids.length;
      const avgLng =
        centroids.reduce((sum, centroid) => sum + centroid[1], 0) /
        centroids.length;
      return [avgLng, avgLat];
    }
    return null;
  }, []);

  const polygonStyle = useCallback(
    feature => ({
      fillColor: selectedPolygons.some(
        polygon => polygon.id === feature.properties.id
      )
        ? 'rgba(58, 235, 52, 0.5)'
        : 'transparent',
      weight: 2,
      opacity: 1,
      color: 'white',
      fillOpacity: 0.7
    }),
    [selectedPolygons]
  );

  const handlePolygonSearch = useCallback(
    event => {
      event.preventDefault();
      const projectIds = selectedPolygons.map(poly => poly.id);
      handleSearch(projectIds);
    },
    [selectedPolygons, handleSearch]
  );

  const handleRemovePolygon = useCallback(index => {
    setSelectedPolygons(prevPolygons =>
      prevPolygons.filter((_, idx) => idx !== index)
    );
  }, []);

  const memoizedGeoJSON = useMemo(
    () => (
      <GeoJSON
        data={data}
        key={JSON.stringify(data)}
        onEachFeature={(feature, layer) => {
          layer.on({
            click: () => handlePolygonClick(feature)
          });
        }}
        style={polygonStyle}
      />
    ),
    [data, handlePolygonClick, polygonStyle]
  );

  const memoizedMarkers = useMemo(
    () =>
      data.map((feature, index) => {
        if (index === 0) return null;
        const position = getPolygonCentroid(feature);
        if (!position) return null;
        return (
          <Marker
            key={feature.properties.id}
            position={position}
            eventHandlers={{
              mouseover: e => e.target.openPopup(),
              mouseout: e => e.target.closePopup(),
              click: e => {
                e.originalEvent.stopPropagation();
                handlePolygonClick(feature);
                e.target.openPopup();
              }
            }}
          >
            <Popup autoClose={false} closeButton={false} closeOnClick={false}>
              <div
                style={{ display: 'flex', flexDirection: 'column', margin: 0 }}
              >
                <div>
                  <p className="m-0">
                    Farm ID: {feature.properties.project_id}
                  </p>
                </div>
                <div>
                  <p className="m-0">
                    Farm Land: {feature.properties.project_name}
                  </p>
                </div>
                <div>
                  <p className="m-0">
                    Farmer: {feature.properties.farmer_name}
                  </p>
                </div>
                <div>
                  <p className="m-0">Area: {feature.properties.project_area}</p>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      }),
    [data, getPolygonCentroid, handlePolygonClick]
  );

  return (
    <div
      style={{
        overflow: 'hidden',
        height: '500px',
        backgroundColor: 'red',
        position: 'relative',
        borderRadius: 10,
        marginBottom: 50
      }}
    >
      <MapContainer
        style={{ height: '100%', width: '100%' }}
        zoom={10}
        zoomSnap={0.5}
        center={[0, 0]}
        radius={200}
      >
        <TileLayer
          attribution='Google Maps &copy; <a href="https://farmsio.com">Farmsio</a>'
          url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
          subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
        />
        {memoizedGeoJSON}
        <CenterMapOnPolygon geoJsonData={data} />
        {memoizedMarkers}
      </MapContainer>
      {!isEmpty(selectedPolygons) && (
        <div style={boxStyle}>
          <h4>Selected Farms</h4>
          {selectedPolygons.map((polygon, idx) => (
            <div
              key={polygon.id}
              style={{
                background: 'white',
                padding: '10px',
                marginBottom: '8px',
                display: 'flex',
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                borderRadius: 8,
                position: 'relative'
              }}
            >
              <div
                style={{
                  flexDirection: 'column',
                  display: 'flex',
                  alignItems: 'flex-start',
                  flex: 1
                }}
              >
                <p
                  style={{
                    color: 'black',
                    fontSize: '14px',
                    margin: 0,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    lineHeight: '1.2'
                  }}
                >
                  {polygon.farmLandID}
                </p>
                <p
                  style={{
                    color: 'gray',
                    fontSize: '10px',
                    margin: 0,
                    textAlign: 'left',
                    lineHeight: '1.2'
                  }}
                >
                  Farm Land: {polygon.farmLandName}
                </p>
                <p
                  style={{
                    color: 'gray',
                    fontSize: '10px',
                    margin: 0,
                    textAlign: 'left',
                    lineHeight: '1.2'
                  }}
                >
                  Farmer: {polygon.farmerName}
                </p>
              </div>
              <p
                style={{
                  color: 'black',
                  fontSize: '14px',
                  margin: 0,
                  marginTop: 15,
                  textAlign: 'right',
                  lineHeight: '1.2',
                  flex: 1
                }}
              >
                Area: {polygon.area}
              </p>
              <IoIosClose
                style={{
                  position: 'absolute',
                  top: 5,
                  right: 5,
                  cursor: 'pointer'
                }}
                onClick={() => handleRemovePolygon(idx)}
              />
            </div>
          ))}
          {selectedPolygons.length === 3 && (
            <p style={{ color: 'red', fontSize: '0.8rem' }}>
              Max limit reached
            </p>
          )}
          <Button
            style={{
              width: '87%',
              position: 'absolute',
              bottom: 16,
              left: 20
            }}
            onClick={handlePolygonSearch}
          >
            Search
          </Button>
        </div>
      )}
    </div>
  );
});

export default FarmSelectionComponent;
